import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    QuestionnaireItemPartContentProps,
    QuestionnairePartContentProps,
    QuestionnaireItemContextType,
    QuestionnaireContextType
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const QuestionnaireContext = createContext<QuestionnaireContextType | undefined>(undefined);

export const QuestionnaireItemContext = createContext<QuestionnaireItemContextType | undefined>(undefined);

export const useQuestionnaireContext = ({
    part
}: {
    part: string;
}): QuestionnaireContextType | undefined => {
    const context = useContext(QuestionnaireContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Questionnaire.${part}: must be rendered inside <Questionnaire>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const useQuestionnaireItemContext = ({
    part
}: {
    part: string;
}): QuestionnaireItemContextType | undefined => {
    const context = useContext(QuestionnaireItemContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Questionnaire.${part}: must be rendered inside <Questionnaire.Item>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withQuestionnaireContext = <P extends object>(
    part: string,
    Content: ComponentType<QuestionnairePartContentProps<P>>
): FC<P> => {
    const QuestionnairePart: FC<P> = (props) => {
        const context = useQuestionnaireContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    QuestionnairePart.displayName = `Questionnaire.${part}`;

    return QuestionnairePart;
};

export const withQuestionnaireItemContext = <P extends object>(
    part: string,
    Content: ComponentType<QuestionnaireItemPartContentProps<P>>
): FC<P> => {
    const QuestionnaireItemPart: FC<P> = (props) => {
        const context = useQuestionnaireContext({
            part
        });

        const itemContext = useQuestionnaireItemContext({
            part
        });

        if(!context || !itemContext) {
            return null;
        }

        return <Content
            {...props}
            itemContext={itemContext}
            context={context}
        />;
    };

    QuestionnaireItemPart.displayName = `Questionnaire.${part}`;

    return QuestionnaireItemPart;
};
